import type { Engine } from "../../../engine/Engine";
import type {
  PointerGestureMoveDetail,
  PointerGesturePressDetail,
  PointerGestureReleaseDetail,
} from "../../../input/PointerGestureInterpreter";
import type { SugarModel } from "../../../loader/SugarModel";
import type IRoomDrawer from "../types/IRoomDrawer";
import type { DesignerPointerInteraction } from "../interaction/DesignerPointerInteraction";
import { ProductTargetPicker } from "../interaction/pick/ProductTargetPicker";
import { UnifiedSegmentTargetPicker } from "../interaction/pick/SegmentTargetPicker";
import { resolveVisualTarget } from "../interaction/pick/VisualDepthResolver";
import { pointerXzOnFloor } from "../segment/tools/planePointer";
import type { DesignerTransformer } from "../../../core/drag/DesignerTransformer";
import type { DesignerToolManager } from "../tools/DesignerToolManager";
import type { MeasurementApi } from "./MeasurementApi";
import { ProductMeasureSession } from "./ProductMeasureSession";
import { ProductDesignerObject } from "../object/ProductDesignerObject";
import { SegmentDesignerObject } from "../object/SegmentDesignerObject";

/** Owns product measurement picking, preview, commit and Escape lifecycle. */
export class MeasureInteractionController implements DesignerPointerInteraction {
  private readonly _productPicker: ProductTargetPicker;
  private readonly _segmentPicker: UnifiedSegmentTargetPicker;
  private _session: ProductMeasureSession | null = null;
  private _source: SugarModel | null = null;
  private _active = false;

  constructor(
    private readonly _engine: Engine,
    private readonly _drawer: IRoomDrawer,
    transformer: DesignerTransformer,
    private readonly _api: MeasurementApi,
    private readonly _tools: DesignerToolManager,
  ) {
    this._productPicker = new ProductTargetPicker(
      transformer.scene,
      _engine,
      () => transformer.selectedModel,
      (model, sourceModel, hit, intersections) =>
        new ProductDesignerObject(
          model,
          sourceModel,
          hit,
          intersections,
          transformer,
          () => false,
        ),
    );
    this._segmentPicker = new UnifiedSegmentTargetPicker(
      _drawer.planarGraphController,
      _drawer.selectInteraction.picker,
      _engine,
      (target, raw, snapped, snappedKinds) =>
        new SegmentDesignerObject(
          target,
          _drawer,
          _engine,
          raw,
          snapped,
          snappedKinds,
          () => false,
          () => {},
        ),
    );
  }

  setSource(source: SugarModel | null): void {
    this._source = source;
    if (!this._active) return;
    this._session?.cancel();
    this._session = source
      ? new ProductMeasureSession(
          source,
          this._api,
          this._engine,
        )
      : null;
  }

  activate(): void {
    if (this._active) return;
    if (!this._source) {
      this._tools.cancel();
      return;
    }
    this._session = new ProductMeasureSession(
      this._source,
      this._api,
      this._engine,
    );
    window.addEventListener("keydown", this._onKeyDown);
    this._active = true;
  }

  deactivate(): void {
    if (!this._active) return;
    window.removeEventListener("keydown", this._onKeyDown);
    this.cancel();
    this._source = null;
    this._active = false;
  }

  press(detail: PointerGesturePressDetail): void {
    if (!this._session || !this._isActivator(detail)) return;
    this._update(detail);
    if (this._session.commit()) this._tools.complete();
    detail.sourceEvent?.stopImmediatePropagation();
  }

  move(detail: PointerGestureMoveDetail): void {
    if (!this._session || detail.activePointerCount > 1) return;
    this._update(detail as PointerGesturePressDetail);
  }

  release(_detail: PointerGestureReleaseDetail): void {}

  cancel(): void {
    this._session?.cancel();
    this._session = null;
  }

  private _update(detail: PointerGesturePressDetail): void {
    const target = resolveVisualTarget([
      this._productPicker.pick(detail),
      this._drawer.segmentToolsEnabled
        ? this._segmentPicker.pick(detail)
        : null,
    ])?.object ?? null;
    this._session?.update(
      target,
      pointerXzOnFloor(detail as any, this._engine),
    );
  }

  private _onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    this._tools.cancel();
  };

  private _isActivator(detail?: PointerGesturePressDetail): boolean {
    if (!detail || !detail.isPrimary) return false;
    return detail.pointerType !== "mouse" ||
      (detail.button === 0 &&
        !detail.modifiers.ctrl &&
        !detail.modifiers.meta &&
        !detail.modifiers.shift);
  }
}
